import React, { forwardRef, useEffect, useRef, useState } from 'react'

// Third party
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import ReactDatePicker from 'react-datepicker'

// Helpers
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid'
import clsx from 'clsx'
import { getNumbersArrBetween, getReactPickerDate } from '../../../../../models/interface.helper'
import { IBasicInputProps } from '../../../../common/inputs/Input'

// Constants
const YEARS = getNumbersArrBetween(1940, new Date().getFullYear())
const DEFAULT_SPECIAL_DATE = new Date(2099, 11, 31) // Default special date

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

interface ICustomDatePicker {
  /** default is 80, if some number is passed then the height of the target element will be incremented by `incrementBy%` of the select menu */
  incrementBy?: number
  /** default is "true", "false" specifies that the height should not be incremented */
  incrementTargetHeight?: false
  /** Classes for target element */
  targetAttributes?: React.HTMLAttributes<HTMLElement>
  /** target id that you want to add classes passed */
  targetId?: string
  /** Whether to show the toggle option */
  showToggle?: boolean
  /** Label for the toggle option */
  toggleLabel?: string
  /** Special date to use when toggle is on */
  specialDate?: Date
  /** Text to display when toggle is on */
  specialDateText?: string
  /** Initial state of the toggle */
  initialToggleState?: boolean
  /** Callback when toggle state changes */
  onToggleChange?: (isChecked: boolean) => void
}

const CustomInput = forwardRef<
  HTMLButtonElement,
  {
    placeholderText?: string
    value?: string
    onClick?: () => void
    className?: string
    isToggleOn?: boolean
    specialDateText?: string
  }
>(({ placeholderText, value, onClick, className, isToggleOn, specialDateText }, ref) => {
  const displayText = isToggleOn
    ? (specialDateText ?? 'Special Date')
    : value
      ? value
      : (placeholderText ?? 'Select Date')

  return (
    <button type="button" className={clsx(className, '')} onClick={onClick} ref={ref}>
      {displayText}
    </button>
  )
})

const SpecialToggleDatePicker = React.forwardRef<any, ICustomDatePicker & IBasicInputProps & any>(
  (props, ref) => {
    // Hooks and vars
    const [yearDropdown, setYearDropdown] = useState(false)
    const yearDropdownRef = useRef<HTMLDivElement>(null)
    const [monthDropdown, setMonthDropdown] = useState(false)
    const [isToggleOn, setIsToggleOn] = useState(props.initialToggleState || false)
    const datePickerRef = useRef<any>(null)

    // Destructure props with defaults
    const {
      showToggle = false,
      toggleLabel = 'Special Date',
      specialDate = DEFAULT_SPECIAL_DATE,
      specialDateText = 'Special Date',
      onToggleChange,
      ...restProps
    } = props

    let localProps = cloneDeep(restProps)
    let className = `text-left text-[14px] custom-date-picker ${props?.className ?? ''}`

    // Check if the selected date is the special date
    useEffect(() => {
      if (props.selected) {
        try {
          const selectedDate = new Date(props.selected)
          if (
            selectedDate.getFullYear() === specialDate.getFullYear() &&
            selectedDate.getMonth() === specialDate.getMonth() &&
            selectedDate.getDate() === specialDate.getDate()
          ) {
            setIsToggleOn(true)
          } else {
            setIsToggleOn(false)
          }
        } catch (e) {
          setIsToggleOn(false)
        }
      }
    }, [props.selected, specialDate])

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked
      setIsToggleOn(isChecked)

      // Call the callback if provided
      if (onToggleChange) {
        onToggleChange(isChecked)
      }

      if (isChecked) {
        // When toggling on - use the special date
        if (props.onChange) {
          props.onChange(specialDate)

          // Close datepicker after 500ms only when toggling on
          setTimeout(() => {
            if (datePickerRef.current && datePickerRef.current.setOpen) {
              datePickerRef.current.setOpen(false)
            }
          }, 500)
        }
      } else {
        // When toggling off - clear the date to show placeholder - leave datepicker open
        if (props.onChange) {
          props.onChange(null)
        }
      }
    }

    const scrollYearDown = () => {
      if (yearDropdownRef.current) {
        yearDropdownRef.current.scrollBy({
          top: 100,
          behavior: 'smooth',
        })
      }
    }

    const scrollYearUp = () => {
      if (yearDropdownRef.current) {
        yearDropdownRef.current.scrollBy({
          top: -100,
          behavior: 'smooth',
        })
      }
    }

    localProps.className = clsx(
      'custom-date-picker outline-0 ring-0 rounded-md focus:outline-0 focus:ring-0 text-SeabiscuitDark200ThemeColor overflow-hidden whitespace-nowrap truncate w-full',
      props.disabled ? 'cursor-default' : 'cursor-pointer',
      className,
      props.textColorOnDisabled,
      props.valid ? props.validValueClassName : props.invalidValueClassName
    )
    localProps.selected = getReactPickerDate(localProps?.selected)

    // Functions
    const handleDatePickerStateChange = async (isOpen: boolean) => {
      await new Promise((resolve) => setTimeout(() => resolve(true), 100))

      if (!props?.targetId) return

      let height = 0
      let valueOfCurrAttrubute: any = null
      let incrementBy = props?.incrementBy ?? 80
      let datePicker = document.querySelector('.react-datepicker')
      let attributes: React.HTMLAttributes<HTMLElement> | null
      let targetElement = document.querySelector(`#${props?.targetId}`) ?? null

      attributes = props?.targetAttributes ?? null
      incrementBy = incrementBy / 100

      if (targetElement) {
        if (attributes)
          Object(attributes).keys((currAttributeKey: keyof typeof attributes) => {
            valueOfCurrAttrubute = attributes?.[currAttributeKey]
            targetElement?.setAttribute?.(currAttributeKey, valueOfCurrAttrubute)
          })

        if (props?.incrementTargetHeight === false) return

        if (isOpen && datePicker) height = datePicker?.clientHeight * incrementBy

        targetElement.setAttribute('style', `margin-bottom: ${height}px`)
      }
    }

    const formatWeekday = (weekdayShort: any) => {
      const customLabels: any = {
        Sunday: 'S',
        Monday: 'M',
        Tuesday: 'T',
        Wednesday: 'W',
        Thursday: 'T',
        Friday: 'F',
        Saturday: 'S',
      }

      return customLabels[weekdayShort]
    }

    const customCalendarContainer = ({ className, children }: any) => {
      return (
        <div className={className} style={{ display: 'flex', flexDirection: 'column' }}>
          {children}
          {showToggle && (
            <div
              style={{
                paddingTop: '12px',
                paddingBottom: '12px',
                borderTop: '1px solid #f2f2f2',
                marginTop: '8px',
                width: '90%',
                alignSelf: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: '4px',
                }}
              >
                <label
                  htmlFor="datepickerToggle"
                  style={{
                    fontSize: '15px',
                    color: '#111827',
                    cursor: props.disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {toggleLabel}
                </label>

                {/* custom circular checkbox wrapper */}
                <div
                  onClick={
                    !props.disabled
                      ? (e) => {
                          e.preventDefault()
                          handleToggle({ target: { checked: !isToggleOn } } as any)
                        }
                      : undefined
                  }
                >
                  {/* hidden actual checkbox for accessibility */}
                  <input
                    type="checkbox"
                    id="datepickerToggle"
                    checked={isToggleOn}
                    onChange={handleToggle}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      pointerEvents: 'none',
                    }}
                    disabled={props.disabled}
                    aria-hidden="false"
                  />

                  {/* visual circular checkbox */}
                  <div
                    style={{
                      height: '20px',
                      width: '20px',
                      borderRadius: '50%',
                      border: '1px solid black',
                      backgroundColor: isToggleOn ? 'black' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: props.disabled ? 'not-allowed' : 'pointer',
                      opacity: props.disabled ? 0.5 : 1,
                      position: 'relative',
                    }}
                    role="presentation"
                  >
                    {isToggleOn && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: 'white' }}
                      >
                        <path
                          d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                          fill="currentColor"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )
    }

    return (
      <ReactDatePicker
        {...restProps}
        ref={(el) => {
          // store the reference locally - using for programatic close
          datePickerRef.current = el

          // forward the ref to parent also
          if (typeof ref === 'function') {
            ref(el)
          } else if (ref) {
            ref.current = el
          }
        }}
        // show null in the calendar when toggle is on, but keep the actual selected value
        selected={isToggleOn ? null : localProps.selected}
        onChange={(date) => {
          if (isToggleOn && !date) {
            // keep the special date when toggle is on
            props.onChange && props.onChange(specialDate)
          } else {
            // normal date selection
            props.onChange && props.onChange(date)
          }
        }}
        onCalendarOpen={() => handleDatePickerStateChange(true)}
        onCalendarClose={() => handleDatePickerStateChange(false)}
        onKeyDown={(e) => e.preventDefault()}
        className={localProps.className}
        formatWeekDay={formatWeekday}
        popperPlacement={props.popperPlacement ?? 'bottom'}
        popperClassName="z-20"
        wrapperClassName={clsx(
          props.wrapperClassName,
          'transition-all flex-1',
          !props.disabled && 'hover:opacity-70'
        )}
        customInput={
          <CustomInput
            className={props.className}
            placeholderText={props.placeholderText}
            isToggleOn={isToggleOn}
            specialDateText={specialDateText}
          />
        }
        renderCustomHeader={({ date, decreaseMonth, increaseMonth, changeMonth, changeYear }) => (
          <div className="px-4 flex justify-between items-center">
            <div className="flex py-4">
              <div className="underline hover:no-underline font-semibold text-SeabiscuitDark200ThemeColor relative inline-block">
                <span
                  onClick={() => {
                    setMonthDropdown(!monthDropdown)
                    setYearDropdown(false)
                  }}
                  className="cursor-pointer"
                >
                  {moment(date).format('MMMM')}
                </span>
                {monthDropdown ? (
                  <div className="max-h-[150px] w-fit flex-col overflow-auto absolute bg-white border border-solid border-SeabiscuitDark200ThemeColor rounded-md text-left p-[5px_10px_0px_10px] cursor-pointer">
                    {MONTHS.map((curr, index) => {
                      return (
                        <div
                          key={index}
                          onClick={() => {
                            changeMonth(index)
                            setMonthDropdown(false)
                          }}
                          className="mb-1"
                        >
                          {curr}
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>
              <div className="underline hover:no-underline font-semibold text-SeabiscuitDark200ThemeColor relative pl-1.5 inline-block">
                <span
                  onClick={() => {
                    setYearDropdown(!yearDropdown)
                    setMonthDropdown(false)
                  }}
                  className="cursor-pointer"
                >
                  {moment(date).format('YYYY')}
                </span>
                {yearDropdown ? (
                  <div className="relative">
                    <div className="absolute border border-solid bg-white border-SeabiscuitDark200ThemeColor h-[182px] w-[52px] rounded-md">
                      <button
                        onClick={scrollYearUp}
                        className="absolute z-10 top-[-6px] left-1/2 transform -translate-x-1/2 text-SeabiscuitDark200ThemeColor p-1 rounded-full"
                      >
                        <ChevronUpIcon className="w-8 h-8" />
                      </button>
                      <div
                        className="max-h-[130px] top-[23px] w-fit flex-col overflow-auto absolute bg-white border-SeabiscuitDark200ThemeColor rounded-md text-left p-[5px_10px_0px_10px] cursor-pointer scrollbar-thin scrollbar-thumb-SeabiscuitDark200ThemeColor scrollbar-track-SeabiscuitGray500ThemeColor"
                        ref={yearDropdownRef}
                      >
                        {YEARS.slice()
                          .reverse()
                          .map((curr) => {
                            return (
                              <div
                                key={curr}
                                onClick={() => {
                                  changeYear(curr)
                                  setYearDropdown(false)
                                }}
                                className="mb-1"
                              >
                                {curr}
                              </div>
                            )
                          })}
                      </div>
                      <button
                        onClick={scrollYearDown}
                        className="absolute z-10 top-[146px] left-1/2 transform -translate-x-1/2 text-SeabiscuitDark200ThemeColor p-1 rounded-full"
                      >
                        <ChevronDownIcon className="w-8 h-8" />
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={decreaseMonth}
                className="w-fit h-fit px-[1px] rounded-full border border-solid border-SeabiscuitGray500ThemeColor"
              >
                <KeyboardArrowLeft fontSize="small" className="text-SeabiscuitDark200ThemeColor" />
              </button>

              <button
                type="button"
                onClick={increaseMonth}
                className="w-fit h-fit px-[1px] rounded-full border border-solid border-SeabiscuitGray500ThemeColor"
              >
                <KeyboardArrowRight fontSize="small" className="text-SeabiscuitDark200ThemeColor" />
              </button>
            </div>
          </div>
        )}
        calendarContainer={customCalendarContainer}
      />
    )
  }
)

export default SpecialToggleDatePicker
